import React from "react";
import lodashGet from "lodash/get";

import { getDistinctObjectValues, getDistinctValues, isEmpty, isFalse, isTrue, toFixed, toFixedSigned } from "../../utils/helpers";
import { GroupType, OState } from "../../utils/types";
import { getEffPartList, getEffectiveExtraPercent, getEffectiveFpoExtraPercent } from "../../helpers/orderHelper";
import { MiscType, OrderFlowType, UOM } from "../../constants";
import { convertTableToCell } from "../../utils/excelHelper";
import { convert } from "../../helpers/unitConversionHelper";

const csvField = (flowInfoList, field) => getDistinctValues(flowInfoList.map(fi => fi[field]).filter(e => !isEmpty(e))).join(", ");

const joinChar = "#@#";

const fpoIdentifier = record => `${record?.primaryUnitId || record?.unitId || ""}${joinChar}${record?.fpo || ""}`;

//* Extract only key from flowInfo which are needed to shown in columns
const sanitizeFlowInfo = (flowInfoList, order) => {
  if (isEmpty(flowInfoList)) return null;
  const flowInfo = flowInfoList[0];
  const productList = getDistinctObjectValues(flowInfoList.map(e => e.product));
  const units = getDistinctValues(flowInfoList.map(e => e.primaryUnitId || e.unitId).filter(e => !isEmpty(e)));
  const fpos = getDistinctValues(flowInfoList.map(e => e.fpo).filter(e => !isEmpty(e)));

  const finalFlowInfoList = flowInfoList.map(fi => ({
    ...fi,
    orderQty: isEmpty(fi.orderParentId) ? fi.orderQty : order?.flowInfoList?.find(e => e.id === fi.orderParentId)?.orderQty || fi.orderQty,
    extra: isEmpty(fi.orderParentId) ? fi.extra : order?.flowInfoList?.find(e => e.id === fi.orderParentId)?.extra || fi.extra
  }));

  return {
    product: productList.length === 1 ? flowInfo.product : null,
    productName: getDistinctValues(flowInfoList.map(fi => fi.product?.name)).join(", "),
    style: getDistinctValues(flowInfoList.map(fi => fi.style)).join(", "),
    color: getDistinctValues(flowInfoList.map(fi => fi.color)).join(", "),
    styleColor: flowInfo.styleColor,
    unitId: units?.length === 1 ? units[0] : units.join(", "),
    fpo: fpos?.length === 1 ? fpos[0] : fpos.join(", "),
    ...["inseam", "destination", "deliveryDate", "customerFlowRef", "flowRef"].reduce(
      (acc, field) => ({ ...acc, [field]: csvField(flowInfoList, field) }),
      {}
    ),
    orderQty: finalFlowInfoList.reduce((acc, fi) => acc + fi.orderQty, 0),
    fpoQty: flowInfoList.reduce((acc, fi) => acc + (fi.fpoQty || 0), 0),
    extra: getEffectiveExtraPercent(finalFlowInfoList),
    fpoExtra: getEffectiveFpoExtraPercent(flowInfoList)
  };
};

export const getReadyToGroupRows = order => {
  if (isEmpty(order?.flowInfoList)) return [];
  const flowInfoList = order.flowInfoList.filter(fi => isEmpty(fi.orderParentId));
  const parentList = flowInfoList.filter(fi => isEmpty(fi.parentId));
  const fpoLevel = order?.fpoLevel || "BPO";

  if (fpoLevel === "COLOR") {
    return parentList.flatMap(parent => {
      const children = flowInfoList.filter(fi => fi.parentId === parent.id);
      const partList = isEmpty(children) ? parent.partList : getEffPartList(children, order);
      const unitChildren = order.flowInfoList.filter(fi => fi.orderParentId === parent.id);
      const fpoIds = getDistinctValues(unitChildren.map(u => fpoIdentifier(u)));

      //TODO: Set handling pending for COLOR level FPO

      // here there may children due to destination multiple unit or due split qty in multiple unit
      return partList
        .sort((a, b) => a.serial - b.serial)
        .flatMap(part => {
          let childList = [];
          let groupOrderList = [];
          let allocOrder;
          if (fpoIds?.length > 1) {
            childList = fpoIds
              .map(fpoId => {
                const fiList = unitChildren.filter(u => fpoIdentifier(u) === fpoId && u.partList?.some(p => p.effPartName === part.effPartName));
                if (isEmpty(fiList)) return {};
                const flowInfo = fiList[0];

                const allocOrder = order?.orderFlowList?.find(
                  of =>
                    of.type === OrderFlowType.ALLOCATION &&
                    of.productIdList?.includes(flowInfo.product?.id) &&
                    of.styleColorList?.includes(flowInfo.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName
                );
                const groupOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.GROUPING)
                  ?.filter(
                    of =>
                      of.productIdList?.includes(flowInfo.product?.id) &&
                      of.styleColorList?.includes(flowInfo.styleColor) &&
                      isEmpty(of.parentColor) &&
                      of.part === part.effPartName &&
                      fpoIdentifier(of) === fpoId &&
                      !isEmpty(of.unitId)
                  );
                return {
                  ...sanitizeFlowInfo(fiList, order),
                  key: `${fiList.map(e => e.id).join("_")}-${part.effPartName}`,
                  parentKey: `${parent.id}-${part.effPartName}`,
                  isFpo: true,
                  order,
                  allocOrder,
                  groupOrderList,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
                };
              })
              .filter(e => !isEmpty(e));

            allocOrder = order?.orderFlowList?.find(
              of =>
                of.type === OrderFlowType.ALLOCATION &&
                of.productIdList?.includes(parent.product?.id) &&
                of.styleColorList?.includes(parent.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === part.effPartName
            );
            groupOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.GROUPING)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName &&
                  fpoIds.includes(fpoIdentifier(of)) &&
                  !isEmpty(of.unitId)
              );
          } else {
            allocOrder = order?.orderFlowList?.find(
              of =>
                of.type === OrderFlowType.ALLOCATION &&
                of.productIdList?.includes(parent.product?.id) &&
                of.styleColorList?.includes(parent.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === part.effPartName
            );
            groupOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.GROUPING)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName &&
                  fpoIdentifier(of) === fpoIds[0] &&
                  !isEmpty(of.unitId)
              );
          }

          const summary = !isEmpty(childList)
            ? sanitizeFlowInfo(childList, order)
            : !isEmpty(unitChildren)
              ? sanitizeFlowInfo(unitChildren, order)
              : sanitizeFlowInfo([parent], order);

          return [
            {
              ...summary,
              key: `${parent.id}-${part.effPartName}`,
              order,
              allocOrder,
              groupOrderList,
              part,
              effPartName: part.effPartName,
              bomCu: part.bomCu,
              ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
              uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
              isParent: !isEmpty(childList)
            },
            ...childList
          ];
        });
    });
  } else {
    return parentList.flatMap(parent => {
      const children = flowInfoList.filter(fi => fi.parentId === parent.id);

      // 1. Simple Order without any child (without destination)
      if (isEmpty(children)) {
        // here there may children due to qty split in multiple unit
        // Flat multiple record due to Part
        const unitChildren = order.flowInfoList.filter(fi => fi.orderParentId === parent.id);
        return parent.partList
          .sort((a, b) => a.serial - b.serial)
          .flatMap(part => {
            const childList = unitChildren.map(child => {
              const allocOrder = order?.orderFlowList?.find(
                of =>
                  of.type === OrderFlowType.ALLOCATION &&
                  of.productIdList?.includes(child.product?.id) &&
                  of.styleColorList?.includes(child.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName
              );
              const groupOrderList = order?.orderFlowList
                ?.filter(of => of.type === OrderFlowType.GROUPING)
                ?.filter(
                  of =>
                    of.productIdList?.includes(child.product?.id) &&
                    of.styleColorList?.includes(child.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName &&
                    fpoIdentifier(of) === fpoIdentifier(child) &&
                    !isEmpty(of.unitId)
                );
              return {
                ...sanitizeFlowInfo([child], order),
                key: `${child.id}-${part.effPartName}`,
                parentKey: `${parent.id}-${part.effPartName}`,
                isFpo: !isEmpty(child.orderParentId),
                order,
                allocOrder,
                groupOrderList,
                part,
                effPartName: part.effPartName,
                bomCu: part.bomCu,
                ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
              };
            });
            const allocOrder = order?.orderFlowList?.find(
              of =>
                of.type === OrderFlowType.ALLOCATION &&
                of.productIdList?.includes(parent.product?.id) &&
                of.styleColorList?.includes(parent.styleColor) &&
                isEmpty(of.parentColor) &&
                of.part === part.effPartName
            );
            const groupOrderList = order?.orderFlowList
              ?.filter(of => of.type === OrderFlowType.GROUPING)
              ?.filter(
                of =>
                  of.productIdList?.includes(parent.product?.id) &&
                  of.styleColorList?.includes(parent.styleColor) &&
                  isEmpty(of.parentColor) &&
                  of.part === part.effPartName &&
                  fpoIdentifier(of) === fpoIdentifier(parent) &&
                  !isEmpty(of.unitId)
              );
            const summary = !isEmpty(childList) ? sanitizeFlowInfo(childList, order) : sanitizeFlowInfo([parent], order);
            return [
              {
                ...summary,
                key: `${parent.id}-${part.effPartName}`,
                order,
                allocOrder,
                groupOrderList,
                part,
                effPartName: part.effPartName,
                bomCu: part.bomCu,
                ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                isParent: !isEmpty(childList)
              },
              ...childList
            ];
          });
      }

      // 2. Set Order whithout destination
      if (isTrue(parent.isSetParent)) {
        // Ideally for Set, Each child will be different based on product, style and color
        const styleIds = getDistinctValues(children.map(c => `${c.product?.id}-${c.style}-${c.color}`));
        const childList = styleIds.flatMap(styleId => {
          const list = children.filter(c => styleId === `${c.product?.id}-${c.style}-${c.color}`);
          const partList = getEffPartList(list, order);
          const child = list[0];
          const unitChildren = order.flowInfoList.filter(fi => fi.orderParentId === child.id);

          // here there may children due to qty split in multiple unit
          return partList
            .sort((a, b) => a.serial - b.serial)
            .flatMap(part => {
              const deepChildList = unitChildren.map(deepChild => {
                const allocOrder = order?.orderFlowList?.find(
                  of =>
                    of.type === OrderFlowType.ALLOCATION &&
                    of.productIdList?.includes(deepChild.product?.id) &&
                    of.styleColorList?.includes(deepChild.styleColor) &&
                    of.parentColor === parent.color &&
                    of.part === part.effPartName
                );
                const groupOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.GROUPING)
                  ?.filter(
                    of =>
                      of.productIdList?.includes(deepChild.product?.id) &&
                      of.styleColorList?.includes(deepChild.styleColor) &&
                      of.parentColor === parent.color &&
                      of.part === part.effPartName &&
                      fpoIdentifier(of) === fpoIdentifier(deepChild) &&
                      !isEmpty(of.unitId)
                  );

                return {
                  ...sanitizeFlowInfo([deepChild], order),
                  key: `${deepChild.id}-${part.effPartName}`,
                  parentKey: `${child.id}-${part?.effPartName}`,
                  isFpo: !isEmpty(deepChild.orderParentId),
                  parentColor: parent.color,
                  parentProductId: parent.product?.id,
                  order,
                  allocOrder,
                  groupOrderList,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
                };
              });
              const allocOrder = order?.orderFlowList?.find(
                of =>
                  of.type === OrderFlowType.ALLOCATION &&
                  of.productIdList?.includes(child.product?.id) &&
                  of.styleColorList?.includes(child.styleColor) &&
                  of.parentColor === parent.color &&
                  of.part === part.effPartName
              );
              const groupOrderList = order?.orderFlowList
                ?.filter(of => of.type === OrderFlowType.GROUPING)
                ?.filter(
                  of =>
                    of.productIdList?.includes(child.product?.id) &&
                    of.styleColorList?.includes(child.styleColor) &&
                    of.parentColor === parent.color &&
                    of.part === part.effPartName &&
                    fpoIdentifier(of) === fpoIdentifier(child) &&
                    !isEmpty(of.unitId)
                );
              const summary = !isEmpty(deepChildList) ? sanitizeFlowInfo(deepChildList, order) : sanitizeFlowInfo([child], order);
              return [
                {
                  ...summary,
                  key: `${child.id}-${part?.effPartName}`,
                  parentKey: `${parent.id}`,
                  parentColor: parent.color,
                  parentProductId: parent.product?.id,
                  order,
                  allocOrder,
                  groupOrderList,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                  isParent: !isEmpty(deepChildList)
                },
                ...deepChildList
              ];
            });
        });
        return [{ ...sanitizeFlowInfo([parent], order), key: `${parent.id}`, order, isParent: true }, ...childList];
      } else {
        const isNonSet = children.every(c => isFalse(c.isSetParent));

        // 3. Simple Order with destination
        if (isNonSet) {
          const partList = getEffPartList(children, order);
          const unitChildren = children.flatMap(child => {
            const childList = order.flowInfoList.filter(fi => fi.orderParentId === child.id);
            return isEmpty(childList) ? [child] : childList;
          });
          const fpoIds = getDistinctValues(unitChildren.map(u => fpoIdentifier(u)));

          // here there may children due to destination multiple unit or due split qty in multiple unit
          return partList
            .sort((a, b) => a.serial - b.serial)
            .flatMap(part => {
              let childList = [];
              let groupOrderList = [];
              let allocOrder;
              if (fpoIds?.length > 1) {
                childList = fpoIds
                  .map(fpoId => {
                    const fiList = unitChildren.filter(u => fpoIdentifier(u) === fpoId && u.partList?.some(p => p.effPartName === part.effPartName));
                    if (isEmpty(fiList)) return {};
                    const flowInfo = fiList[0];

                    const allocOrder = order?.orderFlowList?.find(
                      of =>
                        of.type === OrderFlowType.ALLOCATION &&
                        of.productIdList?.includes(flowInfo.product?.id) &&
                        of.styleColorList?.includes(flowInfo.styleColor) &&
                        isEmpty(of.parentColor) &&
                        of.part === part.effPartName
                    );
                    const groupOrderList = order?.orderFlowList
                      ?.filter(of => of.type === OrderFlowType.GROUPING)
                      ?.filter(
                        of =>
                          of.productIdList?.includes(flowInfo.product?.id) &&
                          of.styleColorList?.includes(flowInfo.styleColor) &&
                          isEmpty(of.parentColor) &&
                          of.part === part.effPartName &&
                          fpoIdentifier(of) === fpoId &&
                          !isEmpty(of.unitId)
                      );
                    return {
                      ...sanitizeFlowInfo(fiList, order),
                      key: `${fiList.map(e => e.id).join("_")}-${part.effPartName}`,
                      parentKey: `${parent.id}-${part.effPartName}`,
                      isFpo: true,
                      order,
                      allocOrder,
                      groupOrderList,
                      part,
                      effPartName: part.effPartName,
                      bomCu: part.bomCu,
                      ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                      uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
                    };
                  })
                  .filter(e => !isEmpty(e));

                allocOrder = order?.orderFlowList?.find(
                  of =>
                    of.type === OrderFlowType.ALLOCATION &&
                    of.productIdList?.includes(parent.product?.id) &&
                    of.styleColorList?.includes(parent.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName
                );
                groupOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.GROUPING)
                  ?.filter(
                    of =>
                      of.productIdList?.includes(parent.product?.id) &&
                      of.styleColorList?.includes(parent.styleColor) &&
                      isEmpty(of.parentColor) &&
                      of.part === part.effPartName &&
                      fpoIds.includes(fpoIdentifier(of)) &&
                      !isEmpty(of.unitId)
                  );
              } else {
                allocOrder = order?.orderFlowList?.find(
                  of =>
                    of.type === OrderFlowType.ALLOCATION &&
                    of.productIdList?.includes(parent.product?.id) &&
                    of.styleColorList?.includes(parent.styleColor) &&
                    isEmpty(of.parentColor) &&
                    of.part === part.effPartName
                );
                groupOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.GROUPING)
                  ?.filter(
                    of =>
                      of.productIdList?.includes(parent.product?.id) &&
                      of.styleColorList?.includes(parent.styleColor) &&
                      isEmpty(of.parentColor) &&
                      of.part === part.effPartName &&
                      fpoIdentifier(of) === fpoIds[0] &&
                      !isEmpty(of.unitId)
                  );
              }

              const summary = !isEmpty(childList)
                ? sanitizeFlowInfo(childList, order)
                : !isEmpty(unitChildren)
                  ? sanitizeFlowInfo(unitChildren, order)
                  : sanitizeFlowInfo([parent], order);
              return [
                {
                  ...summary,
                  key: `${parent.id}-${part.effPartName}`,
                  order,
                  allocOrder,
                  groupOrderList,
                  part,
                  effPartName: part.effPartName,
                  bomCu: part.bomCu,
                  ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                  uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                  isParent: !isEmpty(childList)
                },
                ...childList
              ];
            });
        }
        // 4. Set Order with destination
        else {
          const deepChildren = children.flatMap(child => {
            const childList = flowInfoList.filter(fi => fi.parentId === child.id);
            return isEmpty(childList) ? [child] : childList;
          });
          const styleIds = getDistinctValues(deepChildren.map(c => `${c.product?.id}-${c.style}-${c.color}`));
          const childList = styleIds.flatMap(styleId => {
            const list = deepChildren.filter(c => styleId === `${c.product?.id}-${c.style}-${c.color}`);
            const partList = getEffPartList(list, order);

            const unitChildren = list.flatMap(child => {
              const childList = order.flowInfoList.filter(fi => fi.orderParentId === child.id);
              return isEmpty(childList) ? [child] : childList;
            });
            const fpoIds = getDistinctValues(unitChildren.map(u => fpoIdentifier(u)));

            // here there may children due to destination multiple unit or due split qty in multiple unit
            return partList
              .sort((a, b) => a.serial - b.serial)
              .flatMap(part => {
                let deepChildList = [];
                if (fpoIds?.length > 1) {
                  deepChildList = fpoIds
                    .map(fpoId => {
                      const fiList = unitChildren.filter(
                        u => fpoIdentifier(u) === fpoId && u.partList?.some(p => p.effPartName === part.effPartName)
                      );
                      if (isEmpty(fiList)) return {};
                      const deepChild = fiList[0];

                      const allocOrder = order?.orderFlowList?.find(
                        of =>
                          of.type === OrderFlowType.ALLOCATION &&
                          of.productIdList?.includes(deepChild.product?.id) &&
                          of.styleColorList?.includes(deepChild.styleColor) &&
                          of.parentColor === parent.color &&
                          of.part === part.effPartName
                      );
                      const groupOrderList = order?.orderFlowList
                        ?.filter(of => of.type === OrderFlowType.GROUPING)
                        ?.filter(
                          of =>
                            of.productIdList?.includes(deepChild.product?.id) &&
                            of.styleColorList?.includes(deepChild.styleColor) &&
                            of.parentColor === parent.color &&
                            of.part === part.effPartName &&
                            fpoIdentifier(of) === fpoIdentifier(deepChild) &&
                            !isEmpty(of.unitId)
                        );
                      return {
                        ...sanitizeFlowInfo(fiList, order),
                        key: `${fiList.map(e => e.id).join("_")}-${part.effPartName}`,
                        parentKey: `${list.map(e => e.id).join("_")}-${part?.effPartName}`,
                        isFpo: true,
                        parentColor: parent.color,
                        parentProductId: parent.product?.id,
                        order,
                        allocOrder,
                        groupOrderList,
                        part,
                        effPartName: part.effPartName,
                        bomCu: part.bomCu,
                        ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                        uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom
                      };
                    })
                    .filter(e => !isEmpty(e));
                }
                const child = list[0];

                const allocOrder = order?.orderFlowList?.find(
                  of =>
                    of.type === OrderFlowType.ALLOCATION &&
                    of.productIdList?.includes(child.product?.id) &&
                    of.styleColorList?.includes(child.styleColor) &&
                    of.parentColor === parent.color &&
                    of.part === part.effPartName
                );
                const groupOrderList = order?.orderFlowList
                  ?.filter(of => of.type === OrderFlowType.GROUPING)
                  ?.filter(
                    of =>
                      of.productIdList?.includes(child.product?.id) &&
                      of.styleColorList?.includes(child.styleColor) &&
                      of.parentColor === parent.color &&
                      of.part === part.effPartName &&
                      of.unitId === child.unitIds &&
                      of.fpo === child.fpo &&
                      !isEmpty(of.unitId)
                  );

                const summary = !isEmpty(deepChildList)
                  ? sanitizeFlowInfo(deepChildList, order)
                  : !isEmpty(unitChildren)
                    ? sanitizeFlowInfo(unitChildren, order)
                    : sanitizeFlowInfo(unitChildren, order);
                return [
                  {
                    ...summary,
                    key: `${list.map(e => e.id).join("_")}-${part?.effPartName}`,
                    parentKey: `${parent.id}`,
                    parentColor: parent.color,
                    parentProductId: parent.product?.id,
                    order,
                    allocOrder,
                    groupOrderList,
                    part,
                    effPartName: part.effPartName,
                    bomCu: part.bomCu,
                    ocu: part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: UOM.GRAM.value, uomOut: UOM.KILOGRAM.value }) : part.ocu,
                    uom: part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom,
                    isParent: !isEmpty(deepChildList)
                  },
                  ...deepChildList
                ];
              });
          });

          return [
            {
              ...sanitizeFlowInfo([parent], order),
              key: `${parent.id}`,
              order,
              isParent: true
            },
            ...childList
          ];
        }
      }
    });
  }
};

export const getGroupOrderRows = order => {
  if (isEmpty(order?.orderFlowList) || isEmpty(order?.flowInfoList)) return [];

  // Here hierarchy will be due to Set Order only not for Unit

  const setGroupOrderList = order.orderFlowList.filter(
    flow => flow.type === OrderFlowType.GROUPING && !isEmpty(flow.parentColor) && flow.stateValue > OState.FABRIC_ALLOCATED.stateValue
  );
  const groupOrderList = order.orderFlowList.filter(
    flow => flow.type === OrderFlowType.GROUPING && isEmpty(flow.parentColor) && flow.stateValue > OState.FABRIC_ALLOCATED.stateValue
  );

  const setParentIds = getDistinctValues(order.flowInfoList.filter(fi => fi.isSetParent).map(fi => `${fi.product?.id}-${fi.style}-${fi.color}`));

  const setRowList = setParentIds.flatMap(setParentId => {
    const flowInfoList = order.flowInfoList.filter(fi => fi.isSetParent).filter(fi => `${fi.product?.id}-${fi.style}-${fi.color}` === setParentId);
    const parentIds = flowInfoList.map(fi => fi.id);
    const childFlowInfoList = order.flowInfoList.filter(fi => isEmpty(fi.orderParentId) && parentIds.includes(fi.parentId));
    const childProductIds = getDistinctValues(childFlowInfoList.map(e => e.product?.id));
    const childColors = getDistinctValues(childFlowInfoList.map(e => e.color));
    const parentColor = flowInfoList[0]?.color;
    const parentStyle = flowInfoList[0]?.style;

    const groupOrderList = setGroupOrderList.filter(
      e =>
        childProductIds.some(id => e.productIdList.includes(id)) &&
        e.styles === parentStyle &&
        childColors.includes(e.colors) &&
        e.parentColor === parentColor
    );

    const parentKey = `flow-info-${flowInfoList.map(e => e.id).join("-")}`;
    const parent = { ...sanitizeFlowInfo(flowInfoList, order), key: parentKey, isParent: true };

    const childList = groupOrderList.map(groupOrder => {
      const flowInfoList = order.flowInfoList
        //TODO: Remove parentColor identification logic when parentColor in FlowInfo is handled properly
        .map(fi => {
          let parentColor;
          if (!isEmpty(fi.orderParentId)) {
            const orderParent = order.flowInfoList.find(e => e.id === fi.orderParentId);
            if (!isEmpty(orderParent?.parentId)) {
              const setParent = order.flowInfoList.find(e => e.id === orderParent.parentId && e.isSetParent);
              parentColor = setParent?.color;
            }
          } else if (!isEmpty(fi.parentId)) {
            const setParent = order.flowInfoList.find(e => e.id === fi.parentId && e.isSetParent);
            parentColor = setParent?.color;
          }
          return { ...fi, parentColor };
        })
        .filter(
          fi =>
            groupOrder.productIdList?.includes(fi.product?.id) &&
            groupOrder.styles === fi.style &&
            groupOrder.colors === fi.color &&
            groupOrder.parentColor === fi.parentColor &&
            fpoIdentifier(groupOrder) === fpoIdentifier(fi)
        );

      return {
        ...sanitizeFlowInfo(flowInfoList, order),
        parentKey,
        key: groupOrder.id,
        groupOrder,
        flowInfoList,
        effPartName: groupOrder.part,
        lotValue: groupOrder.lotValue
      };
    });

    return [parent, ...childList];
  });

  const rowList = groupOrderList.map(groupOrder => {
    const flowInfoList = order.flowInfoList.filter(
      fi =>
        groupOrder.productIdList?.includes(fi.product?.id) &&
        groupOrder.styleColorList?.includes(fi.styleColor) &&
        fpoIdentifier(groupOrder) === fpoIdentifier(fi)
    );
    return {
      ...sanitizeFlowInfo(flowInfoList, order),
      key: groupOrder.id,
      groupOrder,
      flowInfoList,
      effPartName: groupOrder.part,
      lotValue: groupOrder.lotValue
    };
  });

  return [...setRowList, ...rowList];
};

export const getDeviation = group => {
  if (!group || !group.deviatedRolls || group.deviatedRolls.length === 0) return "";
  const total = group.deviatedRolls.reduce((acc, el) => acc + el.length, 0);
  return `${toFixed(total, 1)}m(${group.deviatedRolls.length}Rolls)`;
};

export const getGroupName = group => {
  if (!group) return "";
  let name = "";
  if (group.type === GroupType.FINAL) {
    const width = group.widthAxis.toFixed(1) + "CM";
    const warp = group.warpPattern !== null && group.weftPattern !== null ? ", L" + toFixedSigned(group.warpPattern, 1) + "%" : "";
    const weft = group.warpPattern !== null && group.weftPattern !== null ? "W" + toFixedSigned(group.weftPattern, 1) + "%" : "";
    const csv = `CSV=${group.csv}`;
    name = `${group.name} (${width}${warp} ${weft}, ${csv})`;
  } else if (group.type === GroupType.SHRINKAGE) {
    const warp = group.warpPattern !== null && group.weftPattern !== null ? "L" + toFixedSigned(group.warpPattern, 1) + "%" : "";
    const weft = group.warpPattern !== null && group.weftPattern !== null ? "W" + toFixedSigned(group.weftPattern, 1) + "%" : "";
    name = `${group.name} (${warp}${weft})`;
  }
  return name;
};

export const getMarkerGroupName = (group, setting) => {
  if (!group) return "";
  let name = "";
  if (group.type === GroupType.FINAL) {
    let width = group.widthAxis.toFixed(1) + "CM";
    const isCPL = !isEmpty(setting?.others) && Array.isArray(setting.others) && setting.others.includes(MiscType.CPL);
    if (isCPL && !isEmpty(setting?.cplWeftShrPercent)) {
      width = `${toFixed(group.widthAxis * 0.01 * (100 + setting?.cplWeftShrPercent), 1)}CM`;
    }
    const warp = group.warpPattern !== null && group.weftPattern !== null ? ", L" + toFixedSigned(group.warpPattern, 1) + "%" : "";
    const weft = group.warpPattern !== null && group.weftPattern !== null ? "W" + toFixedSigned(group.weftPattern, 1) + "%" : "";
    const csv = `CSV=${group.csv}`;
    name = `${group.name} (${width}${warp} ${weft}, ${csv})`;
  } else if (group.type === GroupType.SHRINKAGE) {
    const warp = group.warpPattern !== null && group.weftPattern !== null ? "L" + toFixedSigned(group.warpPattern, 1) + "%" : "";
    const weft = group.warpPattern !== null && group.weftPattern !== null ? "W" + toFixedSigned(group.weftPattern, 1) + "%" : "";
    name = `${group.name} (${warp}${weft})`;
  }
  return name;
};

export const patternValue = group => {
  if (!isEmpty(group?.patternNumber)) {
    return !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
      ? `L${toFixedSigned(group.warpPattern, 1)}% W${toFixedSigned(group.weftPattern, 1)}% (${group.patternNumber})`
      : "-";
  } else {
    return !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
      ? `L${toFixedSigned(group.warpPattern, 1)}% W${toFixedSigned(group.weftPattern, 1)}%`
      : "-";
  }
};

export const parseMarkerName = markerName => {
  if (isEmpty(markerName)) return { sizeGroup: "", name: "" };
  if (!markerName.includes("_")) return { sizeGroup: "", name: markerName };
  const sizeGroup = getDistinctValues(markerName.split(", ").map(e => e.split("_")[0]));
  const name = markerName
    ?.split(", ")
    .map(e => e.split("_")[1])
    .join(", ");

  return { sizeGroup: sizeGroup[0], name };
};

export const markerGroupName = marker => {
  if (!isEmpty(marker?.group)) return getGroupName(marker.group);
  let items = [`${marker.width || ""} CM`];
  if (!isEmpty(marker.warpPattern) && !isEmpty(marker.weftPattern)) {
    items.push(`L${toFixedSigned(marker.warpPattern, 1)}% W${toFixedSigned(marker.weftPattern, 1)}%`);
  }
  if (!isEmpty(marker.csv) && marker.csv.toLowerCase() !== "no") {
    items.push(`CSV=${marker.csv}`);
  }
  if (!isEmpty(marker.shade)) {
    items.push(`Shade=${marker.shade}`);
  }
  return items.join(", ");
};

export const shrinkageRange = (group, key) => {
  let result;
  if (group[`${key}Axis`] !== undefined && group[`${key}Variance`] !== undefined) {
    result = `(${toFixedSigned(group[`${key}Axis`], 1)} to ${toFixedSigned(group[`${key}Axis`] + group[`${key}Variance`], 1)})`;
  }
  return result;
};

export const shrinkageValue = (group, key) => {
  if (!group || !key) return null;
  const splits = key.replace(/([a-z])([A-Z])/g, "$1 $2").split(" "); //for spliting camel case
  return group[`warp${splits[1]}`] === null && group[`weft${splits[1]}`] === null
    ? "NIL"
    : splits[1] === "Mean"
      ? toFixedSigned(-group[key], 1) + "%"
      : toFixedSigned(group[key], 1) + "%";
};

export const getShrinkageValue = group => {
  if (isEmpty(group?.warpPattern) || isEmpty(group?.weftPattern)) return "";
  return `${toFixedSigned(group.warpPattern)}L,${toFixedSigned(group.weftPattern)}`;
};

export const getFinalOption = summarySelected => {
  return {
    ...summarySelected,
    groupList: summarySelected.groupList.map(group => ({
      ...group,
      width: `${group.widthAxis} (${toFixed(group.widthAxis, 1)} - ${toFixed(group.widthAxis + group.widthVariance, 1)})`,
      warpSystem: shrinkageValue(group, "warpMean"),
      weftSystem: shrinkageValue(group, "weftMean"),
      warpManual: shrinkageValue(group, "warpPattern"),
      weftManual: shrinkageValue(group, "weftPattern"),
      csvValue: group.csv,
      fabricQty: toFixed(group.totalFabric, 1),
      deviations: getDeviation(group),
      wastage: `${toFixed(summarySelected.widthWasteLength, 1)} m (${toFixed(summarySelected.widthWastePercent, 2)}%)`
    }))
  };
};

export const getHeader = ({ name, group, groupList }) => {
  let columns = [];
  let isKnitwear = false;
  if (name === "deviation" && group) {
    isKnitwear = group.deviatedRolls?.some(ri => !isEmpty(ri.weight) && ri.weight > 0);
    columns = [
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNumber", title: "Roll Number" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "length", title: "Length" },
      { dataKey: "weight", title: "Weight" },
      { dataKey: "width", title: "Width" },
      {
        dataKey: "warp",
        title: `Warp ${shrinkageRange(group, "warp")}`
      },
      {
        dataKey: "weft",
        title: `Weft ${shrinkageRange(group, "weft")}`
      }
    ];
  } else if (name === "rollInfo" && groupList) {
    isKnitwear = groupList?.flatMap(g => g.rollList || g.rollInfoList || []).some(ri => !isEmpty(ri.weight) && ri.weight > 0);
    columns = [
      { dataKey: "group", title: "Group", rowSpan: ({ record }) => (record.serialNo === 1 ? record.rollCount : 0) },
      {
        dataKey: "interval",
        title: "Interval",
        rowSpan: ({ record }) => (record.serialNo === 1 ? record.rollCount : 0)
      },
      { dataKey: "serialNo", title: "Sl. No." },
      { dataKey: "rollNumber", title: "Roll Number" },
      { dataKey: "length", title: "Length" },
      { dataKey: "weight", title: "Weight" },
      { dataKey: "warpShrinkage", title: "Warp" },
      { dataKey: "weftShrinkage", title: "Weft" }
    ];
  }

  if (isKnitwear) {
    columns = columns.filter(c => c.dataKey !== "length");
  } else {
    columns = columns.filter(c => c.dataKey !== "weight");
  }
  return columns;
};

export const getData = ({ name, group, groupList }) => {
  let rows = [];
  if (name === "deviation" && group && group.deviatedRolls) {
    rows = group.deviatedRolls.map(({ warpShrinkage, weftShrinkage, ...rollInfo }, idx) => {
      let warp = [<span key={1}>{toFixedSigned(warpShrinkage, 1)}</span>];
      let weft = [<span key={1}>{toFixedSigned(weftShrinkage, 1)}</span>];
      if (warpShrinkage < group.warpAxis || warpShrinkage > toFixed(group.warpAxis + group.warpVariance)) {
        let d = warpShrinkage < group.warpAxis ? group.warpAxis - warpShrinkage : warpShrinkage - group.warpAxis - group.warpVariance;
        warp.push(<span key={2}> ({toFixed(d, 1)})</span>);
      }
      if (weftShrinkage < group.weftAxis || weftShrinkage > toFixed(group.weftAxis + group.weftVariance)) {
        let d = weftShrinkage < group.weftAxis ? group.weftAxis - weftShrinkage : weftShrinkage - group.weftAxis - group.weftVariance;
        weft.push(<span key={2}> ({toFixed(d, 1)})</span>);
      }
      return {
        ...rollInfo,
        serialNo: idx + 1,
        warp: <div style={{ color: warp.length === 2 ? "red" : null }}>{warp}</div>,
        weft: <div style={{ color: weft.length === 2 ? "red" : null }}>{weft}</div>
      };
    });
  } else if (name === "rollInfo" && groupList) {
    groupList.forEach((group, idx1) => {
      group.rollList.forEach(({ rollNumber, length, weight, warpShrinkage, weftShrinkage }, idx2) => {
        let interval = [
          <div key={1} style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Warp:</div>
            <div>
              ({toFixedSigned(group.warpAxis, 1)} to {toFixedSigned(group.warpAxis + group.warpVariance, 1)})
            </div>
          </div>,
          <div key={2} style={{ display: "flex", justifyContent: "space-between" }}>
            <div>Weft:</div>
            <div>
              ({toFixedSigned(group.weftAxis, 1)} to {toFixedSigned(group.weftAxis + group.weftVariance, 1)})
            </div>
          </div>
        ];
        rows.push({
          group: `G${idx1 + 1}`,
          interval,
          serialNo: idx2 + 1,
          rollNumber,
          length,
          weight,
          warpShrinkage,
          weftShrinkage,
          rollCount: group.rollList.length
        });
      });
    });
  }
  return rows;
};

export const generateGroupingReportData = ({
  tenantName,
  order,
  orderFlow,
  // poInfoColumns,
  flowInfoColumns,
  flowInfoRows,
  rollInfoList,
  groupList,
  rollFields = [],
  i18nMessages = {}
}) => {
  if (isEmpty(order?.flowInfoList) || isEmpty(orderFlow) || isEmpty(rollInfoList) || isEmpty(groupList)) return null;
  const excludeFields = ["orderQty", "extra", "fabricRequired", "extraFabricRequired", "shadeReady", "shrinkageReady", "qcReady"];
  const finalFlowInfoColumns = flowInfoColumns.filter(c => !excludeFields.includes(c.dataKey));
  // const flowCount = order.flowInfoList?.filter(fi => orderFlow?.flowInfoIds?.includes(fi.id))?.length || 0;
  const maxColCount = finalFlowInfoColumns.length;

  let excelSheetList = [];
  let sheetNo = 0;

  //** Group Summary **//
  if (orderFlow?.state === OState.SHRINKAGE_GROUPING_COMPLETED.state) {
    const { columnList, rowList } = getShrinkageData(groupList, maxColCount);
    const finalMaxColNo = Math.max(maxColCount, columnList.length);

    const cellList = convertTableToCell(finalFlowInfoColumns, flowInfoRows, 3, flowInfoColumns?.length);
    const lastRowNo = cellList.map(e => e.rowNo).sort((a, b) => b - a)[0];

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenantName,
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Fabric Grouping Summary",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...cellList,
      { rowNo: lastRowNo + 1, colNo: 1, value: "Groups", colEnd: finalMaxColNo, cellStyle: { bold: true } }
    ];

    excelSheetList.push({
      sheetNo: sheetNo++,
      sheetName: "Group Summary",
      autoSizeColumn: true,
      excelData: {
        cellList: finalCellList,
        columnList,
        rowList
      }
    });
  } else {
    const { columnList, rowList } = getFinalData(groupList, maxColCount);
    const finalMaxColNo = Math.max(maxColCount, columnList.length);

    const cellList = convertTableToCell(finalFlowInfoColumns, flowInfoRows, 3, flowInfoColumns?.length);
    const lastRowNo = cellList.map(e => e.rowNo).sort((a, b) => b - a)[0];

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenantName,
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Fabric Grouping Summary",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...cellList,
      { rowNo: lastRowNo + 1, colNo: 1, value: "Groups", colEnd: finalMaxColNo, cellStyle: { bold: true } }
    ];

    excelSheetList.push({
      sheetNo: sheetNo++,
      sheetName: "Group Summary",
      autoSizeColumn: true,
      excelData: {
        cellList: finalCellList,
        columnList,
        rowList
      }
    });
  }

  //** Roll Details **//
  const groupMap = groupList.reduce((acc, group) => ({ ...acc, [group.id]: group }), {});
  const rollList = rollInfoList
    .filter(ri => ri.groupOrderId === orderFlow?.id && !isEmpty(ri.finalGroupId))
    .map(ri => ({ ...ri, finalGroup: groupMap[ri.finalGroupId], shrinkageGroup: groupMap[ri.shrinkageGroup] }));
  if (!isEmpty(rollList)) {
    const { columnList, rowList } = getRollData(rollList, maxColCount, rollFields, i18nMessages);
    const finalMaxColNo = Math.max(maxColCount, columnList.length);
    const cellList = convertTableToCell(finalFlowInfoColumns, flowInfoRows, 3, flowInfoColumns?.length);
    const lastRowNo = cellList.map(e => e.rowNo).sort((a, b) => b - a)[0];

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenantName,
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Fabric Grouping Roll Details",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...cellList,
      { rowNo: lastRowNo + 1, colNo: 1, value: "Rolls", colEnd: finalMaxColNo, cellStyle: { bold: true } }
    ];

    excelSheetList.push({
      sheetNo: sheetNo++,
      sheetName: "Roll Details",
      autoSizeColumn: true,
      excelData: {
        cellList: finalCellList,
        columnList,
        rowList
      }
    });
  }

  //** Shrinkage Summary **//
  if (orderFlow?.stateValue > OState.SHRINKAGE_GROUPING_COMPLETED.stateValue && groupList.some(e => e.type === GroupType.SHRINKAGE)) {
    const { columnList, rowList } = getShrinkageData(groupList, maxColCount);
    const finalMaxColNo = Math.max(maxColCount, columnList.length);

    const cellList = convertTableToCell(finalFlowInfoColumns, flowInfoRows, 3, flowInfoColumns?.length);
    const lastRowNo = cellList.map(e => e.rowNo).sort((a, b) => b - a)[0];

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenantName,
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Shrinkage Summary",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...cellList,
      { rowNo: lastRowNo + 1, colNo: 1, value: "Groups", colEnd: finalMaxColNo, cellStyle: { bold: true } }
    ];

    excelSheetList.push({
      sheetNo: sheetNo++,
      sheetName: "Shrinkage Summary",
      autoSizeColumn: true,
      excelData: {
        cellList: finalCellList,
        columnList,
        rowList
      }
    });
  }

  //** Shade Summary **//
  if (!isEmpty(rollList)) {
    const { columnList, rowList } = getShadeData(rollList, maxColCount);
    const finalMaxColNo = Math.max(maxColCount, columnList.length);
    const cellList = convertTableToCell(finalFlowInfoColumns, flowInfoRows, 3, flowInfoColumns?.length);
    const lastRowNo = cellList.map(e => e.rowNo).sort((a, b) => b - a)[0];

    const finalCellList = [
      {
        rowNo: 1,
        colNo: 1,
        value: tenantName,
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
        cellHeight: 32
      },
      {
        rowNo: 2,
        colNo: 1,
        value: "Fabric Grouping Roll Details",
        colEnd: finalMaxColNo,
        cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
        cellHeight: 32
      },
      ...cellList,
      { rowNo: lastRowNo + 1, colNo: 1, value: "Shade Summary", colEnd: finalMaxColNo, cellStyle: { bold: true } }
    ];

    if (!isEmpty(rowList)) {
      excelSheetList.push({
        sheetNo: sheetNo++,
        sheetName: "Shade Summary",
        autoSizeColumn: true,
        excelData: {
          cellList: finalCellList,
          columnList,
          rowList
        }
      });
    }
  }

  return excelSheetList;
};

// const getStyleInfoData = ({ order, orderFlow, poInfoColumns, flowInfoColumns, rollInfoList, maxColNo }) => {
//   let cellList = [];

//   const fiList = order.flowInfoList
//     ?.filter(fi => fi.product?.id === orderFlow.product?.id && orderFlow.styleColorList.includes(fi.styleColor))
//     .filter(fi => isEmpty(orderFlow.parentColor) || orderFlow.parentColor === fi.parentColor)
//     .filter(fi => fpoIdentifier(fi) === fpoIdentifier(orderFlow));

//   const styleIds = fiList.map(fi => `${fi.product?.id}-${fi.style}-${fi.color}`);
//   const flowInfoList = styleIds.map(styleId => {
//     const list = fiList.filter(fi => `${fi.product?.id}-${fi.style}-${fi.color}` === styleId);
//     return sanitizeFlowInfo(list);
//   });

//   if (flowInfoList?.length > 1) {
//     // // PO Details
//     // const poInfo = { ...order, customerName: order?.customer?.name, seasonName: order?.season?.name };
//     // cellList.push({ rowNo: 3, colNo: 1, colEnd: maxColNo, value: "PO Details", cellStyle: { bold: true } });
//     // const poEmptyCols = maxColNo > poInfoColumns.length ? maxColNo - poInfoColumns.length : 0;
//     // cellList = [
//     //   ...cellList,
//     //   ...poInfoColumns.map((c, idx) => ({
//     //     rowNo: 4,
//     //     colNo: idx + 1,
//     //     value: c.title,
//     //     cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//     //   })),
//     //   ...Array(poEmptyCols)
//     //     .fill("")
//     //     .map((_, idx) => ({
//     //       rowNo: 4,
//     //       colNo: poInfoColumns.length + idx + 1,
//     //       cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//     //     }))
//     // ];
//     // cellList = [
//     //   ...cellList,
//     //   ...poInfoColumns.map((c, idx) => ({
//     //     rowNo: 5,
//     //     colNo: idx + 1,
//     //     value: poInfo[c.dataKey],
//     //     cellStyle: { hAlign: "left" }
//     //   })),
//     //   ...Array(poEmptyCols)
//     //     .fill("")
//     //     .map((_, idx) => ({ rowNo: 5, colNo: poInfoColumns.length + idx + 1, cellStyle: { hAlign: "left" } }))
//     // ];

//     // Flow Details
//     const flowRows = flowInfoList.map(flowInfo => {
//       // const part = flowInfo.partList?.find(p => p.partName === orderFlow.part) || {};
//       // const ledgerList = flowInfo?.ledgerList?.filter(l => part?.id === l.part?.id) || [];
//       // const fabricItemIds = getDistinctValues(ledgerList.map(l => l.fabricItem?.id).filter(id => !isEmpty(id)));
//       // const ledger = ledgerList[0];
//       // const itemRollList = rollInfoList.filter(ri => fabricItemIds.includes(ri.supplierRoll?.fabricItemId));

//       // const allocatedRollList = itemRollList.filter(
//       //   ri => ri.groupOrderId === orderFlow?.id && ri.style === flowInfo.style && ri.color === flowInfo.color && isTrue(ri.grouped)
//       // );
//       // const alreadyAllocated = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
//       return {
//         ...flowInfo,
//         productName: flowInfo.product?.name,
//         styleColor: flowInfo.color,
//         lot: orderFlow.lotValue,
//         effPartName: orderFlow.part
//         // itemCode: ledger?.fabric?.itemCode,
//         // fabColor: ledger?.color,
//         // allocated: Number(toFixed(alreadyAllocated, 1))
//       };
//     });
//     cellList.push({ rowNo: 6, colNo: 1, colEnd: maxColNo, value: "Flow Details", cellStyle: { bold: true } });
//     const flowEmptyCols = maxColNo > flowInfoColumns.length ? maxColNo - flowInfoColumns.length : 0;
//     cellList = [
//       ...cellList,
//       ...flowInfoColumns.map((c, idx) => ({
//         rowNo: 7,
//         colNo: idx + 1,
//         value: c.title,
//         cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//       })),
//       ...Array(flowEmptyCols)
//         .fill("")
//         .map((_, idx) => ({
//           rowNo: 7,
//           colNo: flowInfoColumns.length + idx + 1,
//           cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//         }))
//     ];
//     flowRows.forEach((flow, flowIdx) => {
//       cellList = [
//         ...cellList,
//         ...flowInfoColumns.map((c, idx) => ({
//           rowNo: 8 + flowIdx,
//           colNo: idx + 1,
//           value: flow[c.dataKey],
//           cellStyle: { hAlign: "left" }
//         })),
//         ...Array(flowEmptyCols)
//           .fill("")
//           .map((_, idx) => ({
//             rowNo: 8 + flowIdx,
//             colNo: flowInfoColumns.length + idx + 1,
//             cellStyle: { hAlign: "left" }
//           }))
//       ];
//     });
//   } else {
//     // PO Details
//     const columns = [...poInfoColumns, ...flowInfoColumns];

//     const poInfo = { ...order, customerName: order?.customer?.name, seasonName: order?.season?.name };
//     const flowRows = flowInfoList.map(flowInfo => {
//       // const part = flowInfo.partList?.find(p => p.partName === orderFlow.part);
//       // const ledgerList = flowInfo?.ledgerList?.filter(l => part?.id === l.part?.id) || [];
//       // const fabricItemIds = getDistinctValues(ledgerList?.map(l => l.fabricItem?.id).filter(id => !isEmpty(id)));
//       // const ledger = ledgerList[0];
//       // const itemRollList = rollInfoList.filter(ri => fabricItemIds.includes(ri.supplierRoll?.fabricItemId));

//       // const allocatedRollList = itemRollList.filter(
//       //   ri => ri.groupOrderId === orderFlow?.id && ri.style === flowInfo.style && ri.color === flowInfo.color && isTrue(ri.grouped)
//       // );
//       // const alreadyAllocated = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
//       return {
//         ...flowInfo,
//         productName: flowInfo.product?.name,
//         styleColor: flowInfo.color,
//         lot: `Lot${orderFlow.lot}`,
//         lotValue: orderFlow.lotValue,
//         effPartName: orderFlow.part
//         // itemCode: ledger?.fabric?.itemCode,
//         // fabColor: ledger?.color,
//       };
//     });
//     const row = { ...poInfo, ...flowRows[0] };

//     cellList.push({ rowNo: 3, colNo: 1, colEnd: maxColNo, value: "PO Details", cellStyle: { bold: true } });
//     const poEmptyCols = maxColNo > columns.length ? maxColNo - columns.length : 0;
//     cellList = [
//       ...cellList,
//       ...columns.map((c, idx) => ({
//         rowNo: 4,
//         colNo: idx + 1,
//         value: c.title,
//         cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//       })),
//       ...Array(poEmptyCols)
//         .fill("")
//         .map((_, idx) => ({
//           rowNo: 4,
//           colNo: columns.length + idx + 1,
//           cellStyle: { bgColor: "99,172,238", color: "255,255,255", hAlign: "left" }
//         }))
//     ];
//     cellList = [
//       ...cellList,
//       ...columns.map((c, idx) => ({
//         rowNo: 5,
//         colNo: idx + 1,
//         value: row[c.dataKey],
//         cellStyle: { hAlign: "left" }
//       })),
//       ...Array(poEmptyCols)
//         .fill("")
//         .map((_, idx) => ({ rowNo: 5, colNo: columns.length + idx + 1, cellStyle: { hAlign: "left" } }))
//     ];
//   }

//   return cellList;
// };

const getShrinkageData = (groupList, maxColNo) => {
  let columnList = [
    { dataKey: "name", title: "Group" },
    { dataKey: "warpSystem", title: "Warp (System)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "weftSystem", title: "Weft (System)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "warpManual", title: "Warp (Manual)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "weftManual", title: "Weft (Manual)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "patternNumber", title: "Pattern No" },
    { dataKey: "totalFabric", title: "Fabric Qty" },
    { dataKey: "deviations", title: "Deviations" }
  ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({ dataKey: `col${idx}`, colNo: columnList.length + idx + 1 }))
    ];
  }

  const rowList = groupList
    .filter(g => g.type === GroupType.SHRINKAGE)
    .map(group => ({
      ...group,
      warpSystem: toFixedSigned(-group.warpMean, 1),
      weftSystem: toFixedSigned(-group.weftMean, 1),
      warpManual: toFixedSigned(group.warpPattern, 1),
      weftManual: toFixedSigned(group.weftPattern, 1),
      fabricQty: `${toFixed(group.totalFabric, 1)}`,
      deviations: getDeviation(group)
    }))
    .map((group, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({
        dataKey: c.dataKey,
        value: group[c.dataKey]
      }))
    }));

  return { columnList, rowList };
};

const getFinalData = (groupList, maxColNo) => {
  let columnList = [
    { dataKey: "name", title: "Group" },
    { dataKey: "width", title: "Width" },
    { dataKey: "warpSystem", title: "Warp (System)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "weftSystem", title: "Weft (System)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "warpManual", title: "Warp (Manual)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "weftManual", title: "Weft (Manual)", type: "double", cellStyle: { dataFormat: "0.0" } },
    { dataKey: "patternNumber", title: "Pattern No" },
    { dataKey: "csv", title: "CSV" },
    { dataKey: "totalFabric", title: "Fabric Qty" },
    { dataKey: "deviations", title: "Deviations" }
  ].map((c, idx) => ({ ...c, colNo: idx + 1 }));
  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({ dataKey: `col${idx}`, colNo: columnList.length + idx + 1 }))
    ];
  }

  const rowList = groupList
    .filter(g => g.type === GroupType.FINAL)
    .map(group => ({
      ...group,
      width: group.widthAxis,
      warpSystem: toFixedSigned(-group.warpMean, 1),
      weftSystem: toFixedSigned(-group.weftMean, 1),
      warpManual: toFixedSigned(group.warpPattern, 1),
      weftManual: toFixedSigned(group.weftPattern, 1),
      fabricQty: `${toFixed(group.totalFabric, 1)}`,
      deviations: getDeviation(group)
    }))
    .map((group, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({
        dataKey: c.dataKey,
        value: group[c.dataKey]
      }))
    }));

  return { columnList, rowList };
};

const getRollData = (rollInfoList, maxColNo, rollFields, i18nMessages) => {
  let columnList = [
    { dataKey: "group", title: "Group" },
    { dataKey: "widthInterval", title: "Width Interval (CM)" },
    { dataKey: "warpShrinkageRange", title: "Shr. Interval - Length" },
    { dataKey: "weftShrinkageRange", title: "Shr. Interval - Width" },
    { dataKey: "supplierRollNo", title: "Supplier Roll No" },
    { dataKey: "rollNo", title: "Roll No" },
    { dataKey: "consignment", title: "Consignment No" },
    { dataKey: "length", title: "Length" },
    { dataKey: "width", title: "Width" },
    { dataKey: "style", title: "Style" },
    { dataKey: "color", title: "Style Color" },
    { dataKey: "shade", title: "Shade" },
    { dataKey: "csv", title: "CSV" },
    { dataKey: "warpShrPercent", title: "Length Shrinkage (%)", type: "double" },
    { dataKey: "weftShrPercent", title: "Width Shrinkage (%)", type: "double" },
    { dataKey: "skewness", title: "Skewness (%)", type: "double" },
    { dataKey: "patternValues", title: "Pattern Values" },
    { dataKey: "patternNo", title: "Pattern No" }
  ].map((c, idx) => ({ ...c, colNo: idx + 1, title: lodashGet(i18nMessages, `resources.rolls.fields.${c.dataKey}`, c.title)?.toUpperCase() }));

  const styleCount = getDistinctValues(rollInfoList.map(ri => ri.style))?.length;
  const colorCount = getDistinctValues(rollInfoList.map(ri => ri.color))?.length;
  const consignmentCount = getDistinctValues(rollInfoList.map(ri => ri.consignment).filter(e => !isEmpty(e)))?.length;
  if (!rollFields.includes("supplierRollNo")) {
    columnList = columnList.filter(c => c.dataKey !== "supplierRollNo");
  }
  if (!rollFields.includes("consignment")) {
    columnList = columnList.filter(c => c.dataKey !== "consignment");
  }
  if (!rollFields.includes("skewness")) {
    columnList = columnList.filter(c => c.dataKey !== "skewness");
  }
  if (styleCount <= 1) {
    columnList = columnList.filter(c => c.dataKey !== "style");
  }
  if (colorCount <= 1) {
    columnList = columnList.filter(c => c.dataKey !== "color");
  }
  if (consignmentCount === 0) {
    columnList = columnList.filter(c => c.dataKey !== "consignment");
  }
  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({ dataKey: `col${idx}`, colNo: columnList.length + idx + 1 }))
    ];
  }

  const rowList = rollInfoList
    .map(ri => ({ ...ri, group: ri.finalGroup }))
    .map(({ group, ...ri }) => ({
      ...ri,
      sortField: `${group?.name}-${ri.shade || ""}`,
      group: getGroupName(group),
      widthInterval:
        isEmpty(group?.widthAxis) || group?.widthAxis === 0
          ? null
          : `${toFixed(group.widthAxis, 1)} to ${toFixed(group.widthAxis + group.widthVariance, 1)}`,
      warpShrinkageRange: !isEmpty(group?.warpAxis)
        ? `${toFixedSigned(group.warpAxis, 1)}% to ${toFixedSigned(group.warpAxis + group.warpVariance, 1)}%`
        : null,
      weftShrinkageRange: !isEmpty(group?.weftAxis)
        ? `${toFixedSigned(group.weftAxis, 1)}% to ${toFixedSigned(group.weftAxis + group.weftVariance, 1)}%`
        : null,
      patternValues:
        !isEmpty(group?.warpPattern) && !isEmpty(group?.weftPattern)
          ? "L=" + toFixedSigned(group.warpPattern, 1) + "%, W=" + toFixedSigned(group.weftPattern, 1) + "%"
          : "",
      patternNo: group?.patternNumber
    }))
    .sort((a, b) => (a.sortField < b.sortField ? -1 : 1))
    .map((rollInfo, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({
        dataKey: c.dataKey,
        value: rollInfo[c.dataKey]
      }))
    }));

  return { columnList, rowList };
};

const getShadeData = (rollInfoList, maxColNo) => {
  let columnList = [
    { dataKey: "shade", title: "Shade" },
    { dataKey: "rollCount", title: "Number of Rolls" },
    { dataKey: "consignments", title: "Consignments" },
    { dataKey: "fabricQty", title: "Fabric Qty" }
  ].map((c, idx) => ({ ...c, colNo: idx + 1, title: c.title.toUpperCase() }));
  const emptyCols = maxColNo > columnList.length ? maxColNo - columnList.length : 0;
  if (emptyCols > 0) {
    columnList = [
      ...columnList,
      ...Array(emptyCols)
        .fill("")
        .map((_, idx) => ({ dataKey: `col${idx}`, colNo: columnList.length + idx }))
    ];
  }

  const shadeList = getDistinctValues(rollInfoList.map(ri => ri.shade).filter(shade => !isEmpty(shade))).sort((a, b) => (a < b ? -1 : 1));
  const rowList = shadeList
    .map(shade => {
      const rollList = rollInfoList.filter(ri => ri.shade === shade);
      const consignments = getDistinctValues(rollList.map(ri => ri.consignment).filter(e => !isEmpty(e))).join(", ");
      const fabricQty = rollList.reduce((acc, ri) => acc + ri.length, 0);
      return {
        shade,
        rollCount: rollList?.length,
        consignments,
        fabricQty
      };
    })
    .map((group, idx) => ({
      rowNo: idx + 1,
      cellList: columnList.map(c => ({
        dataKey: c.dataKey,
        value: group[c.dataKey]
      }))
    }));

  return { columnList, rowList };
};
